import CloseButton from "@/components/button/close-button";
import { useCrimeSceneStore } from "@/store/crimeSceneStore";
import { findScoreResult } from "@/utils/simulation";
import { useMemo } from "react";
import Text from "@/components/text/text";
import Image from "@/components/image";
import { useNavigate } from "react-router-dom";
import useMdBreakpoint from "@/hooks/useMdBreakpoint";

type Props = {
  score: number;
  maxScore: number;
  size?: number;
  stroke?: number;
};

function CircularScore({ score, maxScore, size = 200, stroke = 12 }: Props) {
  const clamped = Math.max(0, Math.min(score, maxScore));
  const center = size / 2;
  const radius = center - stroke / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = clamped / maxScore;
  const dashOffset = circumference * (1 - progress);

  // Unique gradient id to avoid collisions
  const gradientId = useMemo(
    () => `grad-${Math.random().toString(36).slice(2)}`,
    []
  );

  return (
    <div className="flex flex-col items-center">
      <div className="relative mt-4">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <defs>
            {/* Use userSpaceOnUse so x/y are in the same coordinate system as the circle */}
            <linearGradient
              id={gradientId}
              gradientUnits="userSpaceOnUse"
              x1={stroke}
              y1={center}
              x2={size - stroke}
              y2={center}
            >
              <stop offset="0%" stopColor="#00D9D2" />
              <stop offset="100%" stopColor="#83F219" />
            </linearGradient>
          </defs>

          {/* Track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#0B433A"
            strokeWidth={stroke}
            strokeLinecap="round"
            opacity={0.6}
          />

          {/* Progress arc (start at 12 o'clock by rotating -90deg around center) */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ transition: "stroke-dashoffset 600ms ease" }}
          />
        </svg>

        {/* Center label */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-1">
            <Text text="score" size={12} />
            <div className="flex">
              <Text
                className="text-500"
                text={clamped.toString()}
                translate={false}
                size={40}
              />
              <Text
                className="text-500"
                text={`/${maxScore}`}
                translate={false}
                size={40}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SimulationScore() {
  const setShowMCPanel = useCrimeSceneStore((state) => state.setShowMCPanel);
  const mcScore = useCrimeSceneStore((state) => state.mcScore);
  const setSceneIndex = useCrimeSceneStore((state) => state.setSceneIndex);
  const setProgress = useCrimeSceneStore((state) => state.setProgress);
  const resetSceneScore = useCrimeSceneStore((state) => state.resetSceneScore);
  const setIsShownIncidentOccurence = useCrimeSceneStore(
    (state) => state.setIsShownIncidentOccurence
  );
  const setIsFinishedAllMc = useCrimeSceneStore(
    (state) => state.setIsFinishedAllMc
  );
  let navigate = useNavigate();

  const result = findScoreResult(mcScore);
  const isMd = useMdBreakpoint();
  const maxScore = 15;

  const filledStars = new Array(result?.stars ?? 0).fill(
    "/assets/icon_star_filled.png"
  );
  const emptyStars = new Array(5 - (result?.stars ?? 0)).fill(
    "/assets/icon_star_empty.png"
  );

  const stars = filledStars.concat(emptyStars);

  const playAgain = () => {
    resetSceneScore();
    setSceneIndex(0);
    setProgress(0);
    setShowMCPanel(false);
    setIsFinishedAllMc(false);
  };

  const review = () => {
    setShowMCPanel(false);
    setIsShownIncidentOccurence(true);
  };

  return (
    <div className="flex flex-col h-full p-[20px]">
      <div className="flex justify-end">
        <CloseButton goBack={false} onClick={() => navigate("/")} />
      </div>
      <div className={`flex flex-col ${isMd ? "mb-[20px]" : "flex-1"}`}>
        <Text
          text={result?.text ?? ""}
          size={24}
          className="text-center text-500"
        />
        <div className="flex justify-center gap-[8px] mt-[32px] mb-[40px]">
          {stars.map((star, index) => {
            return (
              <Image
                src={star}
                alt="icon"
                width={52}
                height={52}
                key={`img-star-${index}`}
              />
            );
          })}
        </div>

        <CircularScore score={mcScore} maxScore={maxScore} />
        <div className="flex w-full  justify-center">
          <button
            onClick={() => review()}
            className="flex items-center btn-style btn-review justify-center w-[280px]"
          >
            <Image
              src="/assets/icon_replay.png"
              alt="icon"
              width={22}
              height={22}
            />
            <Text
              text="btn_review"
              size={17}
              className="text-center text-500 primary-text ml-[8px]"
            />
          </button>
        </div>
      </div>
      <div className="flex md:items-end justify-center">
        <button
          className="btn-style primary-btn w-[280px] md:max-w-[280px]"
          onClick={() => playAgain()}
        >
          <Text text="btn_play_again" />
        </button>
      </div>
    </div>
  );
}
